import React, { useMemo } from 'react';

import { Route, createRoutesFromElements } from 'react-router-dom';

import { RemoteComponent, TabTitle } from '@adsk/exo-skeleton-react';

import { getEnvConfig } from '../../envs/envConfig';
import {
  FullPageRoute,
  LAYOUTS,
  ProjectsRoute,
  PublicRoute,
  ToolRoute,
} from '../../envs/types';
import AppContentError from '../AppContentError/AppContentError';
import ExoSkeletonApi from '../ExoSkeletonApi/ExoSkeletonApi';
import { ContentLayoutLoader } from '../FullPageLayoutLoader/ContentLayoutLoader';
import { FullPageLayoutLoader } from '../FullPageLayoutLoader/FullPageLayoutLoader';
import PageNotFoundExo from '../PageNotFoundExo/PageNotFoundExo';
import { PageNotFoundRedirect } from '../PageNotFoundRedirect/PageNotFoundRedirect';
import Redirect from '../Redirect/Redirect';
import RegexRedirect from '../Redirect/RegexRedirect';
import RemoteTool from '../RemoteTool/RemoteTool';
import { RootErrorBoundary } from '../RootErrorBoundary';

const RootProjectPageLayout = React.lazy(
  () => import('../RootProjectPageLayout'),
);

const RootPublicPageLayout = React.lazy(
  () => import('../RootPublicPageLayout'),
);

const RootProjectDirectoryLayout = React.lazy(
  () => import('../RootProjectDirectoryLayout'),
);

const RootFullPageLayout = React.lazy(() => import('../RootFullPageLayout'));

const stringRedirects = [
  {
    from: '/build/projects/project-templates',
    to: '/projects/templates',
  },
  {
    from: '/projects/project-templates',
    to: '/projects/templates',
  },
  {
    from: '/build/projects',
    to: '/projects',
  },
  {
    from: '/projects/projects',
    to: '/projects',
  },
  /* These redirects have been showing on our infra, moving it here to collect more information about these requests */
  {
    from: '/login',
    to: '/projects',
  },
  {
    from: '/build/',
    to: '/projects',
  },
  {
    from: '/',
    to: '/projects',
  },
];

const regexRedirects = [
  {
    // Match any path like /project-admin/projects/{projectId}/locations
    from: /^\/project-admin\/projects\/([^/]+)\/locations$/,
    // Redirect to /project-admin/locations/projects/{projectId}
    to: (match: RegExpMatchArray) =>
      `/project-admin/locations/projects/${match[1]}`,
    path: '/project-admin/projects/:projectId/locations',
  },
];

const useLayoutRoutes = () => {
  const { remoteRoutes } = useMemo(() => getEnvConfig(), []);
  return useMemo(() => {
    const projectPageLayoutRoutes = remoteRoutes.filter(
      (route): route is ToolRoute =>
        route.layout === LAYOUTS.PROJECT_PAGE_LAYOUT,
    );
    const projectDirectoryLayoutRoutes = remoteRoutes.filter(
      (route): route is ProjectsRoute =>
        route.layout === LAYOUTS.PROJECT_DIRECTORY_LAYOUT,
    );

    const publicPageLayoutRoutes = remoteRoutes.filter(
      (route): route is PublicRoute =>
        route.layout === LAYOUTS.PUBLIC_PAGE_LAYOUT,
    );

    const fullPageLayoutRoutes = remoteRoutes.filter(
      (route): route is FullPageRoute =>
        route.layout === LAYOUTS.FULL_PAGE_LAYOUT,
    );

    return {
      projectPageLayoutRoutes,
      projectDirectoryLayoutRoutes,
      publicPageLayoutRoutes,
      fullPageLayoutRoutes,
    };
  }, [remoteRoutes]);
};

export const useRoutes = () => {
  const {
    projectPageLayoutRoutes,
    projectDirectoryLayoutRoutes,
    publicPageLayoutRoutes,
    fullPageLayoutRoutes,
  } = useLayoutRoutes();

  const routesObject = useMemo(
    () =>
      createRoutesFromElements(
        <Route element={<RootErrorBoundary />}>
          <Route element={<ExoSkeletonApi />}>
            <Route element={<RootProjectPageLayout />}>
              {projectPageLayoutRoutes.map(
                ({
                  path,
                  scope,
                  modulePath,
                  customTitle,
                  layoutProps: { currentToolId },
                }) => {
                  return (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <>
                          {customTitle ?? <TabTitle />}
                          <RemoteTool
                            scope={scope}
                            moduleName={modulePath}
                            toolId={currentToolId}
                          />
                        </>
                      }
                    />
                  );
                },
              )}
            </Route>
            <Route element={<RootProjectDirectoryLayout />}>
              {projectDirectoryLayoutRoutes.map(
                ({ path, scope, modulePath }) => {
                  return (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <RemoteComponent
                          scope={scope}
                          moduleName={modulePath}
                          renderLoader={() => <ContentLayoutLoader />}
                          renderError={() => <AppContentError />}
                        />
                      }
                    />
                  );
                },
              )}
            </Route>
            <Route element={<RootPublicPageLayout />}>
              {publicPageLayoutRoutes.map(
                ({ path, scope, modulePath, customTitle }) => {
                  return (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <>
                          {customTitle}
                          <RemoteComponent
                            scope={scope}
                            moduleName={modulePath}
                          />
                        </>
                      }
                    />
                  );
                },
              )}
            </Route>
            <Route element={<RootFullPageLayout />}>
              {fullPageLayoutRoutes.map(({ path, scope, modulePath }) => {
                return (
                  <Route
                    key={path}
                    path={path}
                    element={
                      <RemoteComponent
                        scope={scope}
                        moduleName={modulePath}
                        renderLoader={() => <FullPageLayoutLoader />}
                      />
                    }
                  />
                );
              })}
              {stringRedirects.map(({ from, to }) => (
                <Route
                  key={from}
                  path={from}
                  element={<Redirect from={from} to={to} />}
                />
              ))}
              {regexRedirects.map(({ from, to, path }) => (
                <Route
                  key={from.toString()}
                  path={path}
                  element={<RegexRedirect pattern={from} toGenerator={to} />}
                />
              ))}
              <Route path="/page-not-found" element={<PageNotFoundExo />} />
              <Route path="*" element={<PageNotFoundRedirect />} />
            </Route>
          </Route>
        </Route>,
      ),
    [
      projectPageLayoutRoutes,
      projectDirectoryLayoutRoutes,
      publicPageLayoutRoutes,
      fullPageLayoutRoutes,
    ],
  );

  return {
    routesObject,
  };
};
